import { Add, Delete } from '@tunasong/icons'
import type { GearCategory, GearRequirement, MonitorPreference, ProfileGear } from '@tunasong/schemas'
import { gearCategoryValues, monitorPreferenceValues } from '@tunasong/schemas'
import {
  useUserConfig,
  Button,
  IconButton,
  Chip,
  Typography,
  TextField,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
  FormControl,
  Select,
} from '@tunasong/ui-lib'
import { useMemo, type ChangeEvent, type FC } from 'react'
import { useCallback, useState } from 'react'
import styles from './gear-config.module.css'

const defaultGearRequirement: GearRequirement = {
  category: 'microphone',
  name: '',
  quantity: 1,
  providedByArtist: false,
}

const categoryLabels: Record<GearCategory, string> = {
  microphone: 'Microphone',
  monitor: 'Monitor',
  stand: 'Stand',
  instrument: 'Instrument',
  backline: 'Backline',
  accessory: 'Accessory',
  other: 'Other',
}

const monitorLabels: Record<MonitorPreference, string> = {
  wedge: 'Wedge Monitor',
  iem: 'In-Ear Monitor (IEM)',
  both: 'Both Wedge and IEM',
  none: 'No Monitor',
}

export const GearConfig: FC = () => {
  const [gear, updateGear] = useUserConfig('gear')
  const [newItem, setNewItem] = useState<GearRequirement>(defaultGearRequirement)

  const currentGear: ProfileGear = useMemo(() => gear ?? { requirements: [] }, [gear])

  const handleUpdateField = useCallback(
    <K extends keyof ProfileGear>(field: K, value: ProfileGear[K]) => {
      updateGear({ ...currentGear, [field]: value })
    },
    [currentGear, updateGear]
  )

  const handleAddRequirement = useCallback(() => {
    if (!newItem.name.trim()) {
      return
    }
    const requirements = [...(currentGear.requirements ?? []), newItem]
    updateGear({ ...currentGear, requirements })
    setNewItem(defaultGearRequirement)
  }, [currentGear, newItem, updateGear])

  const handleRemoveRequirement = useCallback(
    (index: number) => {
      const requirements = currentGear.requirements?.filter((_, i) => i !== index) ?? []
      updateGear({ ...currentGear, requirements })
    },
    [currentGear, updateGear]
  )

  return (
    <div>
      <Typography variant="h6" className={styles.sectionTitle}>
        Gear Profile
      </Typography>
      <Typography variant="body2" color="textSecondary" className={styles.description}>
        Configure your default gear requirements for technical riders. These settings will be used when generating
        riders for bandspaces you&apos;re a member of.
      </Typography>

      {/* Role */}
      <TextField
        fullWidth
        label="Role / Position"
        placeholder="e.g., Vocals, Lead Guitar, Drums"
        value={currentGear.role ?? ''}
        onChange={e => handleUpdateField('role', e.target.value || undefined)}
        className={styles.description}
      />

      {/* Monitor Preference */}
      <FormControl fullWidth className={styles.description}>
        <Select
          label="Monitor Preference"
          value={currentGear.monitorPreference ?? ''}
          onChange={(e: ChangeEvent<HTMLSelectElement>) =>
            handleUpdateField('monitorPreference', (e.target.value as MonitorPreference) || undefined)
          }
        >
          <option value="">Not specified</option>
          {monitorPreferenceValues.map(pref => (
            <option key={pref} value={pref}>
              {monitorLabels[pref]}
            </option>
          ))}
        </Select>
      </FormControl>

      {/* Notes */}
      <TextField
        fullWidth
        multiline
        rows={2}
        label="Notes for Sound Engineers"
        placeholder="Any special requirements or preferences..."
        value={currentGear.notes ?? ''}
        onChange={e => handleUpdateField('notes', e.target.value || undefined)}
        className={styles.description}
      />

      {/* Gear Requirements */}
      <Typography variant="h6" className={styles.sectionTitle}>
        Gear Requirements
      </Typography>

      {currentGear.requirements?.length ? (
        <Table className={styles.table}>
          <TableHead>
            <TableRow>
              <TableCell>Category</TableCell>
              <TableCell>Item</TableCell>
              <TableCell>Qty</TableCell>
              <TableCell>Provided</TableCell>
              <TableCell width={50}></TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {currentGear.requirements.map((req, index) => (
              <TableRow key={index}>
                <TableCell>
                  <Chip size="small" label={categoryLabels[req.category]} />
                </TableCell>
                <TableCell>
                  {req.name}
                  {req.notes ? (
                    <Typography variant="caption" className={styles.blockCaption} color="textSecondary">
                      {req.notes}
                    </Typography>
                  ) : null}
                </TableCell>
                <TableCell>{req.quantity}</TableCell>
                <TableCell>{req.providedByArtist ? 'By artist' : 'By venue'}</TableCell>
                <TableCell>
                  <IconButton size="small" onClick={() => handleRemoveRequirement(index)}>
                    <Delete size={16} />
                  </IconButton>
                </TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      ) : (
        <Typography variant="body2" color="textSecondary" className={styles.noItems}>
          No gear requirements added yet.
        </Typography>
      )}

      {/* Add new requirement */}
      <div className={styles.formRow}>
        <FormControl className={styles.categorySelect}>
          <Select
            label="Category"
            value={newItem.category}
            onChange={(e: ChangeEvent<HTMLSelectElement>) =>
              setNewItem({ ...newItem, category: e.target.value as GearCategory })
            }
          >
            {gearCategoryValues.map(cat => (
              <option key={cat} value={cat}>
                {categoryLabels[cat]}
              </option>
            ))}
          </Select>
        </FormControl>
        <TextField
          size="small"
          label="Item name"
          placeholder="e.g., SM58"
          value={newItem.name}
          onChange={e => setNewItem({ ...newItem, name: e.target.value })}
          className={styles.flexField}
        />
        <TextField
          size="small"
          label="Qty"
          type="number"
          value={newItem.quantity}
          onChange={e => setNewItem({ ...newItem, quantity: parseInt(e.target.value, 10) || 1 })}
          className={styles.qtyField}
          min={1}
        />
        <FormControl className={styles.categorySelect}>
          <Select
            label="Provided by"
            value={newItem.providedByArtist ? 'artist' : 'venue'}
            onChange={(e: ChangeEvent<HTMLSelectElement>) =>
              setNewItem({ ...newItem, providedByArtist: e.target.value === 'artist' })
            }
          >
            <option value="venue">Venue</option>
            <option value="artist">Artist</option>
          </Select>
        </FormControl>
        <Button variant="contained" startIcon={<Add />} onClick={handleAddRequirement} disabled={!newItem.name.trim()}>
          Add
        </Button>
      </div>

      <TextField
        size="small"
        fullWidth
        label="Notes for this item"
        placeholder="e.g., boom stand preferred"
        value={newItem.notes ?? ''}
        onChange={e => setNewItem({ ...newItem, notes: e.target.value || undefined })}
        className={styles.noItems}
      />
    </div>
  )
}

export default GearConfig
